/**
 * Argentina Provinces Template Configuration
 * Similar to Spain's system but for Argentine provinces
 */

export type TemplateTypeAR = "BaseTemplate" | "BioLinkTemplate";

export interface ArgentinaProvinceConfig {
  provinceCode: string;
  provinceName: string;
  template: TemplateTypeAR;
  timezone: string[];
  enabled: boolean;
  // Additional province-specific data
  region: "CABA" | "GBA" | "INTERIOR" | "PATAGONIA" | "NOA" | "NEA" | "CUYO";
  population: number;
  economicLevel: "high" | "medium" | "low";
}

/**
 * Argentina Provinces Template Configuration
 * 
 * Template Logic:
 * - BaseTemplate: All Argentine provinces use BaseTemplate to maintain consistency with LATAM countries
 * - Argentina as a LATAM country uses BaseTemplate by default (same as most LATAM countries)
 */
export const ARGENTINA_PROVINCES: ArgentinaProvinceConfig[] = [
  // Ciudad Autónoma de Buenos Aires (CABA)
  {
    provinceCode: "CABA",
    provinceName: "Ciudad Autónoma de Buenos Aires",
    template: "BaseTemplate", // Consistent with Argentina as LATAM country
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "CABA",
    population: 3075646,
    economicLevel: "high",
  },
  
  // Provincia de Buenos Aires
  {
    provinceCode: "BA", 
    provinceName: "Buenos Aires",
    template: "BaseTemplate", // Consistent with Argentina as LATAM country
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "GBA",
    population: 17569053,
    economicLevel: "high",
  },
  
  // Córdoba
  {
    provinceCode: "CB",
    provinceName: "Córdoba", 
    template: "BaseTemplate", // Consistent with Argentina as LATAM country
    timezone: ["America/Argentina/Cordoba"],
    enabled: true,
    region: "INTERIOR",
    population: 3978984,
    economicLevel: "high",
  },
  
  // Santa Fe
  {
    provinceCode: "SF",
    provinceName: "Santa Fe",
    template: "BaseTemplate", // Consistent with Argentina as LATAM country
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "INTERIOR", 
    population: 3397532,
    economicLevel: "high",
  },
  
  // Mendoza
  {
    provinceCode: "MZ",
    provinceName: "Mendoza",
    template: "BaseTemplate", // Tourism and wine, medium income
    timezone: ["America/Argentina/Mendoza"],
    enabled: true,
    region: "CUYO",
    population: 2014533,
    economicLevel: "medium",
  },
  
  // Tucumán
  {
    provinceCode: "TM",
    provinceName: "Tucumán",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Tucuman"],
    enabled: true,
    region: "NOA",
    population: 1687305,
    economicLevel: "medium",
  },
  
  // Entre Ríos
  {
    provinceCode: "ER",
    provinceName: "Entre Ríos", 
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "INTERIOR",
    population: 1426426,
    economicLevel: "medium",
  },
  
  // Salta
  {
    provinceCode: "SA",
    provinceName: "Salta",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Salta"],
    enabled: true,
    region: "NOA", 
    population: 1440672,
    economicLevel: "medium",
  },
  
  // Misiones
  {
    provinceCode: "MN",
    provinceName: "Misiones",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "NEA",
    population: 1280960,
    economicLevel: "medium",
  },
  
  // Chaco
  {
    provinceCode: "CC",
    provinceName: "Chaco",
    template: "BaseTemplate", 
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "NEA",
    population: 1204541,
    economicLevel: "low",
  },
  
  // Corrientes
  {
    provinceCode: "CN",
    provinceName: "Corrientes",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "NEA",
    population: 1197553,
    economicLevel: "medium",
  },
  
  // Santiago del Estero
  {
    provinceCode: "SE",
    provinceName: "Santiago del Estero",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "NOA",
    population: 978313,
    economicLevel: "low",
  },
  
  // San Juan
  {
    provinceCode: "SJ",
    provinceName: "San Juan", 
    template: "BaseTemplate",
    timezone: ["America/Argentina/San_Juan"],
    enabled: true,
    region: "CUYO",
    population: 818234,
    economicLevel: "medium",
  },
  
  // Jujuy
  {
    provinceCode: "JY",
    provinceName: "Jujuy",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Jujuy"],
    enabled: true,
    region: "NOA",
    population: 811811,
    economicLevel: "low",
  },
  
  // Río Negro
  {
    provinceCode: "RN",
    provinceName: "Río Negro",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "PATAGONIA", 
    population: 747610,
    economicLevel: "medium",
  },
  
  // Neuquén
  {
    provinceCode: "NQ",
    provinceName: "Neuquén",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "PATAGONIA",
    population: 726590,
    economicLevel: "medium",
  },
  
  // Formosa
  {
    provinceCode: "FM",
    provinceName: "Formosa",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "NEA",
    population: 606041,
    economicLevel: "low",
  },
  
  // Chubut
  {
    provinceCode: "CH",
    provinceName: "Chubut", 
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "PATAGONIA",
    population: 603120,
    economicLevel: "medium",
  },
  
  // San Luis
  {
    provinceCode: "SL",
    provinceName: "San Luis",
    template: "BaseTemplate",
    timezone: ["America/Argentina/San_Luis"],
    enabled: true,
    region: "CUYO",
    population: 540905,
    economicLevel: "medium",
  },
  
  // Catamarca
  {
    provinceCode: "CT",
    provinceName: "Catamarca",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Catamarca"],
    enabled: true,
    region: "NOA", 
    population: 429556,
    economicLevel: "low",
  },
  
  // La Rioja
  {
    provinceCode: "LR",
    provinceName: "La Rioja",
    template: "BaseTemplate",
    timezone: ["America/Argentina/La_Rioja"],
    enabled: true,
    region: "NOA",
    population: 393531,
    economicLevel: "low",
  },
  
  // Santa Cruz
  {
    provinceCode: "SC",
    provinceName: "Santa Cruz",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Buenos_Aires"],
    enabled: true,
    region: "PATAGONIA",
    population: 374756,
    economicLevel: "medium",
  },
  
  // Tierra del Fuego
  {
    provinceCode: "TF",
    provinceName: "Tierra del Fuego",
    template: "BaseTemplate",
    timezone: ["America/Argentina/Ushuaia"],
    enabled: true,
    region: "PATAGONIA",
    population: 190641,
    economicLevel: "medium",
  },
];

/**
 * Get province configuration by province code
 */
export const getArgentinaProvinceConfig = (provinceCode: string): ArgentinaProvinceConfig | null => {
  const config = ARGENTINA_PROVINCES.find(
    province => province.provinceCode === provinceCode && province.enabled
  );
  return config || null;
};

/**
 * Nombre de provincia (taxonomía del Worker: "CABA", "Buenos Aires", "Córdoba",
 * …) → provinceCode de este catálogo. Es el puente para que los hooks de
 * template/analytics deriven de la VERDAD ÚNICA de location (el Worker) en vez
 * de detectar por su cuenta. Match accent/case-insensitive.
 */
export const getArgentinaProvinceCodeByName = (name: string | null | undefined): string | null => {
  if (!name) return null;
  const norm = (s: string) =>
    s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const n = norm(name);
  if (!n) return null;
  if (n === "caba" || n.includes("autonoma") || n === "capital federal") return "CABA";
  const match = ARGENTINA_PROVINCES.find((p) => norm(p.provinceName) === n);
  return match ? match.provinceCode : null;
};

/**
 * Get template type for a specific Argentine province
 */
export const getTemplateForArgentinaProvince = (provinceCode: string): TemplateTypeAR => {
  const config = getArgentinaProvinceConfig(provinceCode);
  return config?.template || "BaseTemplate"; // Default fallback
};

/**
 * Check if an Argentine province should use BioLinkTemplate
 */
export const shouldArgentinaProvinceUseBioLink = (provinceCode: string): boolean => {
  return getTemplateForArgentinaProvince(provinceCode) === "BioLinkTemplate";
};

/**
 * Get all Argentine provinces using BioLinkTemplate
 */
export const getArgentinaBioLinkProvinces = (): ArgentinaProvinceConfig[] => {
  return ARGENTINA_PROVINCES.filter(
    province => province.template === "BioLinkTemplate" && province.enabled
  );
};

/**
 * Get all Argentine provinces using BaseTemplate
 */
export const getArgentinaBaseTemplateProvinces = (): ArgentinaProvinceConfig[] => {
  return ARGENTINA_PROVINCES.filter(
    province => province.template === "BaseTemplate" && province.enabled
  );
};

/**
 * Detect Argentine province from timezone
 */
export const detectArgentinaProvinceFromTimezone = (timezone: string): string | null => {
  // Direct timezone matches first (most accurate)
  for (const province of ARGENTINA_PROVINCES) {
    if (province.enabled && province.timezone.includes(timezone)) {
      return province.provinceCode;
    }
  }
  
  // Fallback: partial matches for complex timezone names
  for (const province of ARGENTINA_PROVINCES) {
    if (province.enabled && province.timezone.some(tz => {
      const cityName = tz.split('/')[2]; // For America/Argentina/Buenos_Aires
      return timezone.includes(cityName);
    })) {
      return province.provinceCode;
    }
  }
  
  return null;
};

/**
 * Get province by region
 */
export const getProvincesByRegion = (region: ArgentinaProvinceConfig['region']): ArgentinaProvinceConfig[] => {
  return ARGENTINA_PROVINCES.filter(
    province => province.region === region && province.enabled
  );
};

/**
 * Get high-income provinces (all use BaseTemplate like other LATAM countries)
 */
export const getHighIncomeProvinces = (): ArgentinaProvinceConfig[] => {
  return ARGENTINA_PROVINCES.filter(
    province => province.economicLevel === "high" && province.enabled
  );
};

// Export default configuration for easy access
export default {
  ARGENTINA_PROVINCES,
  getArgentinaProvinceConfig,
  getTemplateForArgentinaProvince,
  shouldArgentinaProvinceUseBioLink,
  getArgentinaBioLinkProvinces,
  getArgentinaBaseTemplateProvinces,
  detectArgentinaProvinceFromTimezone,
  getProvincesByRegion,
  getHighIncomeProvinces,
};